import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthCard } from "./AuthCard";
import { AuthSideContentLogin } from "./AuthSideContent";
import { AuthSideContentSignUp } from "./AuthSideContent";
import { cn } from "@/lib/utils";

export type AuthMode = "login" | "signup";

interface AuthSplitPageProps {
  /** Current mode: login (card left) or signup (card right) */
  mode: AuthMode;
  /** Builds /login with optional query (mode, invite_token). Call with { mode: 'signup' } or { mode: 'login' } to toggle. */
  getLoginPath: (params: { mode?: AuthMode }) => string;
  /** Called when user clicks toggle (smooth in-place update). If set, use instead of Link for toggle. */
  onToggleMode?: (mode: AuthMode) => void;
  /** Auth form card content (login or signup form) */
  children: React.ReactNode;
  /** Optional class for the page container */
  className?: string;
}

const spring = { type: "spring" as const, stiffness: 300, damping: 30 };

export function AuthSplitPage({ mode, getLoginPath, onToggleMode, children, className }: AuthSplitPageProps) {
  const isSignUp = mode === "signup";

  // Desktop: first column = left, second = right (use md:order so columns don't swap).
  // Mobile: card first, then content → when login card is in first div (order-1), when signup card is in second div (order-1).
  const leftOrder = isSignUp ? 2 : 1;
  const rightOrder = isSignUp ? 1 : 2;

  const toggleTo = isSignUp ? "login" : "signup";
  const toggleLabel = isSignUp ? "Already have an account? Log in" : "New here? Create account";

  return (
    <div
      className={cn(
        "min-h-[100dvh] h-screen max-h-[100dvh] flex flex-col bg-background overflow-hidden",
        "relative",
        className
      )}
      role="main"
      aria-label="Sign in or create an account"
    >
      {/* Subtle background gradient blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -right-1/2 w-full h-full rounded-full bg-primary/5 blur-3xl"
          animate={{
            x: isSignUp ? 20 : -10,
            y: 10,
          }}
          transition={{ duration: 0.6, ...spring }}
        />
        <motion.div
          className="absolute -bottom-1/2 -left-1/2 w-full h-full rounded-full bg-primary/[0.07] blur-3xl"
          animate={{
            x: isSignUp ? -20 : 10,
            y: -10,
          }}
          transition={{ duration: 0.6, ...spring }}
        />
      </div>

      {/* Top nav — flex-shrink-0 so main area gets remaining height */}
      <nav className="relative z-10 flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 md:px-8 border-b border-border/50 bg-background/80 backdrop-blur-sm min-w-0">
        <Link to="/" className="text-base sm:text-lg font-semibold text-foreground hover:text-primary transition-colors shrink-0 truncate">
          Stravo
        </Link>
        {onToggleMode ? (
          <button
            type="button"
            onClick={() => onToggleMode(toggleTo)}
            className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors text-right truncate min-w-0 max-w-[60%] sm:max-w-none"
          >
            {toggleLabel}
          </button>
        ) : (
          <Link
            to={getLoginPath({ mode: toggleTo })}
            className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors text-right truncate min-w-0 max-w-[60%] sm:max-w-none"
          >
            {toggleLabel}
          </Link>
        )}
      </nav>

      {/* Main: scroll on mobile so card + side content stack and Create account is visible; desktop single viewport */}
      <div className="relative z-10 flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 overflow-y-auto overflow-x-hidden md:overflow-hidden overscroll-contain">
        {/* Left column (desktop): card when login, content when signup — md:order-1 keeps this in col 1 */}
        <div
          className={cn(
            "flex items-center justify-center p-4 md:p-8 pb-8 md:pb-8 min-h-0 md:min-h-0 overflow-visible md:overflow-hidden min-w-0",
            "md:order-1",
            leftOrder === 1 ? "order-1" : "order-2"
          )}
        >
          <AnimatePresence mode="wait">
            {!isSignUp ? (
              <motion.div
                key="card-left"
                className="w-full max-w-md flex justify-center md:justify-end flex-shrink-0 min-w-0"
                initial={{ opacity: 0, x: -32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -32 }}
                transition={spring}
              >
                <AuthCard side="left">{children}</AuthCard>
              </motion.div>
            ) : (
              <motion.div
                key="content-signup"
                className="w-full max-w-lg flex justify-center md:justify-end min-h-0 overflow-hidden min-w-0"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
                transition={{ duration: 0.25 }}
              >
                <AuthSideContentSignUp />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right column (desktop): content when login, card when signup — md:order-2 keeps this in col 2 */}
        <div
          className={cn(
            "flex items-center justify-center p-4 md:p-8 pb-10 md:pb-8 min-h-0 md:min-h-0 overflow-visible md:overflow-hidden min-w-0",
            "md:order-2",
            rightOrder === 1 ? "order-1" : "order-2"
          )}
        >
          <AnimatePresence mode="wait">
            {!isSignUp ? (
              <motion.div
                key="content-login"
                className="w-full max-w-lg flex justify-center md:justify-start min-h-0 overflow-hidden min-w-0"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.25 }}
              >
                <AuthSideContentLogin />
              </motion.div>
            ) : (
              <motion.div
                key="card-right"
                className="w-full max-w-md flex justify-center md:justify-start flex-shrink-0 min-w-0"
                initial={{ opacity: 0, x: 32 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 32 }}
                transition={spring}
              >
                <AuthCard side="right">{children}</AuthCard>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
